import type { WorldConfig } from '../../config/world.config';
import type { DynamicEventType } from '../../dynamic-events/DynamicEventType';
import type { HazardDefinition } from '../../hazards/HazardDefinition';
import { HazardKind } from '../../hazards/HazardDefinition';
import { laneCenterX, moduleSides, type ModuleSides } from './segmentBuilders';
import type { DynamicEventSocket, FixedObstaclePlacement, ObstacleVariant } from './SegmentDefinition';

/**
 * Helpers for the moving-hazard set pieces (SEG_09 crane area, SEG_10 excavator, SEG_11 forklift,
 * SEG_03 slow truck): event sockets that arm the hazards, mirrored layouts, site sides without
 * the decorations that would stand where a crane or an excavator works.
 */

/**
 * Hazard sockets arm early: the longest timeline (forklift ≈ 5.6 s) needs ~100 m at top speed –
 * the validator checks every hazard against its socket.
 */
export const HAZARD_TRIGGER_DISTANCE = 110;

/** Dynamic event socket that arms a hazard (segment-local position of its key line). */
export function hazardSocket(id: string, x: number, z: number, event: DynamicEventType): DynamicEventSocket {
  return { id, position: { x, y: 0, z }, yaw: 0, supportedEvents: [event], triggerDistance: HAZARD_TRIGGER_DISTANCE, affectedLanes: [0, 1, 2] };
}

/** Lane l ↔ lane (count − 1 − l). */
export function mirrorLane(world: WorldConfig, lane: number): number {
  return world.laneCount - 1 - lane;
}

/** Left/right mirror of obstacle placements (spawn point ids end with _L<lane>). */
export function mirroredPlacements(world: WorldConfig, placements: readonly FixedObstaclePlacement[]): FixedObstaclePlacement[] {
  return placements.map((p) => ({
    ...p,
    spawnPointId: p.spawnPointId.replace(/_L(\d)$/, (_, lane: string) => `_L${mirrorLane(world, Number(lane))}`),
  }));
}

/** Mirrored variant: placements mirrored, hazards swapped for their mirrored counterparts. */
export function mirroredVariant(world: WorldConfig, id: string, variant: ObstacleVariant, hazards: readonly string[]): ObstacleVariant {
  return { id, weight: variant.weight, placements: mirroredPlacements(world, variant.placements), hazards, ...(variant.rowLayout ? { rowLayout: variant.rowLayout } : {}) };
}

/** Left/right mirror of a hazard definition (new id, mirrored lanes and machine positions). */
export function mirroredHazard<H extends HazardDefinition>(world: WorldConfig, id: string, hazard: H): H {
  const lane = (l: number) => mirrorLane(world, l);
  switch (hazard.kind) {
    case HazardKind.CraneBeam:
      return { ...hazard, id, crane: { ...hazard.crane, x: -hazard.crane.x } };
    case HazardKind.CraneBlock:
      return { ...hazard, id, lane: lane(hazard.lane), crane: { ...hazard.crane, x: -hazard.crane.x } };
    case HazardKind.CraneSwing:
      // The mirrored swing sweeps the other way round.
      return { ...hazard, id, lane: lane(hazard.lane), sweep: hazard.sweep === -1 ? 1 : -1, crane: { ...hazard.crane, x: -hazard.crane.x } };
    case HazardKind.Excavator:
      return { ...hazard, id, lane: lane(hazard.lane), machine: { ...hazard.machine, x: -hazard.machine.x } };
    case HazardKind.Vehicle:
      return {
        ...hazard,
        id,
        lane: lane(hazard.lane),
        ...(hazard.laneChange ? { laneChange: { ...hazard.laneChange, toLane: lane(hazard.laneChange.toLane) } } : {}),
        ...(hazard.zigzag ? { zigzag: { ...hazard.zigzag, toward: hazard.zigzag.toward > 0 ? -1 : 1 } } : {}),
      };
  }
}

/**
 * Site sides of a straight module without the listed decoration sockets (a crane or excavator stands
 * there); the roadside bays in `groundProps` shrink to single ground props beside the machine.
 */
export function sidesWithout(length: number, removedSockets: readonly string[], groundProps: readonly string[] = []): ModuleSides {
  const sides = moduleSides(length, { groundProps });
  return { ...sides, decorationSockets: sides.decorationSockets.filter((s) => !removedSockets.includes(s.id)) };
}

/** x of a lane centre – shorthand for the definitions. */
export function laneX(world: WorldConfig, lane: number): number {
  return laneCenterX(world, lane);
}
